import moment from "moment"
import { liveService } from "./liveService"

/**
 * LiveEventBufferBase - Base class for buffering live session events
 *
 * Instead of making an API call for each event (cam on/off, etc.), events are recorded
 * in a buffer and synced to the backend every 60 seconds or when the session ends.
 *
 * This is the shared base class. Platform-specific implementations are in:
 * - LiveEventBuffer.js (mobile/native)
 * - LiveEventBuffer.web.js (web)
 *
 * Usage:
 *   LiveEventBuffer.getInstance().init(liveSessionId, userId)
 *   LiveEventBuffer.getInstance().record('cam_toggle', { enabled: true })
 *   LiveEventBuffer.getInstance().destroy() // on session end
 */

export const SYNC_INTERVAL_MS = 60000 // 60 seconds
export const OUT_OF_APP_DEBOUNCE_MS = 5000 // 5 seconds debounce for out_of_app events

/**
 * Should a failed flush be replayed at the next sync?
 *
 * Yes for anything transient (no response at all, timeout, rate limit, 5xx).
 * No for a 4xx: the batch was refused on its content, so replaying it produces
 * the same rejection forever and every event queued behind it is lost with it.
 *
 * @param {any} error - The error thrown by the API call (axios)
 * @returns {boolean}
 */
export function isRetryableSyncError(error) {
    const status = error?.response?.status
    if (!status) return true // network error / timeout — no response received
    if (status === 408 || status === 429) return true
    return status >= 500
}

export class LiveEventBufferBase {
    constructor() {
        this.events = []
        this.liveSessionId = null
        this.userId = null
        this.syncInterval = null
        // Debounce state for out_of_app events
        this.outOfAppDebounceTimer = null
        this.pendingOutOfAppEvent = null
        // Qualité de la dernière dégradation locale encore ouverte, s'il y en a
        // une : c'est ce buffer qui écrit le journal de la séance, donc c'est à
        // lui de ne pas le laisser finir sur un épisode sans fin.
        this.openQualityEpisode = null
    }

    /**
     * Initialize the buffer with session context
     * @param {number|string} liveSessionId - The live session ID
     * @param {number|string} userId - The current user ID
     */
    init(liveSessionId, userId) {
        // If already initialized for same session, skip
        if (this.liveSessionId === liveSessionId && this.userId === userId) {
            return
        }

        // If switching sessions, flush the old one first
        if (this.liveSessionId && this.liveSessionId !== liveSessionId) {
            this.flush()
        }

        this.liveSessionId = liveSessionId
        this.userId = userId
        this.events = []
        this.openQualityEpisode = null
        this.startSyncInterval()
        this.setupPlatformListeners()
    }

    /**
     * Platform-specific listener setup - override in subclasses
     */
    setupPlatformListeners() {
        // Override in platform-specific implementations
    }

    /**
     * Platform-specific listener cleanup - override in subclasses
     */
    cleanupPlatformListeners() {
        // Override in platform-specific implementations
    }

    /**
     * Record an event to the buffer
     * @param {string} type - Event type (e.g., 'cam_toggle', 'mic_toggle', 'hand_raise')
     * @param {object} data - Event-specific data
     */
    record(type, data = {}) {
        if (!this.liveSessionId || !this.userId) {
            console.warn(
                "LiveEventBuffer: Cannot record event, buffer not initialized"
            )
            return
        }

        this.events.push({
            type,
            user_id: this.userId,
            timestamp: moment.utc().format("YYYY-MM-DD HH:mm:ss"),
            ...data,
        })

        if (type === "quality_degraded") {
            this.openQualityEpisode = data?.quality ?? "poor"
        } else if (type === "quality_recovered") {
            this.openQualityEpisode = null
        }
    }

    /**
     * Referme la dégradation locale en cours, s'il y en a une : la mesure
     * s'arrête, la connexion n'est pas pour autant rétablie — d'où `reason`,
     * que le backend garde pour distinguer les deux. Sans ça, une dégradation
     * ouverte au moment où on quitte le live n'a jamais de fin dans le journal.
     *
     * @param {string} reason - left_live | disconnected | returned_to_app
     */
    closeQualityEpisode(reason) {
        if (!this.openQualityEpisode || !this.liveSessionId) return

        this.record("quality_recovered", {
            quality: this.openQualityEpisode,
            reason,
        })
    }

    /**
     * Record out_of_app_start event with debounce
     * Only records if user stays out of app for 5 seconds
     */
    recordOutOfAppStart() {
        // Clear any pending debounce
        this.clearOutOfAppDebounce()

        // Set up debounced recording
        this.pendingOutOfAppEvent = "start"
        this.outOfAppDebounceTimer = setTimeout(() => {
            if (this.pendingOutOfAppEvent === "start") {
                this.record("out_of_app_start", {})
                this.flush()
                this.pendingOutOfAppEvent = null
            }
        }, OUT_OF_APP_DEBOUNCE_MS)
    }

    /**
     * Record out_of_app_end event with debounce
     * Only records if user was actually recorded as out of app
     */
    recordOutOfAppEnd() {
        // If there's a pending start event, cancel it (user came back within 5 seconds)
        if (this.pendingOutOfAppEvent === "start") {
            this.clearOutOfAppDebounce()
            return
        }

        // Clear any pending debounce and record immediately
        this.clearOutOfAppDebounce()
        this.record("out_of_app_end", {})
    }

    /**
     * Clear the out_of_app debounce timer
     */
    clearOutOfAppDebounce() {
        if (this.outOfAppDebounceTimer) {
            clearTimeout(this.outOfAppDebounceTimer)
            this.outOfAppDebounceTimer = null
        }
        this.pendingOutOfAppEvent = null
    }

    /**
     * Flush events to the backend
     * @returns {Promise<void>}
     */
    async flush() {
        if (this.events.length === 0 || !this.liveSessionId) {
            return
        }

        // Each flush takes the events it sends, so two can overlap without a
        // duplicate. Waiting for the one in flight would lose the last events of
        // a session: `destroy()` detaches the buffer right after its own flush.
        const liveSessionId = this.liveSessionId
        const eventsToSend = [...this.events]
        this.events = [] // Clear buffer immediately to avoid duplicates

        try {
            await liveService.storeEventsBatch(
                {
                    id: liveSessionId,
                },
                {
                    events: eventsToSend,
                }
            )
        } catch (error) {
            // Only while the buffer still writes this session's journal: the
            // final flush of a session that was left must not replay its events
            // into the next one.
            if (
                isRetryableSyncError(error) &&
                this.liveSessionId === liveSessionId
            ) {
                // Network hiccup or server-side error: re-add events to buffer
                // for the next sync attempt.
                this.events = [...eventsToSend, ...this.events]
            }
            // A 4xx means the payload itself was refused — replaying it would
            // fail identically at every flush and block every later event of
            // the session behind it. Drop the batch instead of poisoning the
            // queue.
            console.error("LiveEventBuffer: Failed to sync events:", error)
        }
    }

    /**
     * Start the periodic sync interval
     */
    startSyncInterval() {
        this.stopSyncInterval()
        this.syncInterval = setInterval(() => this.flush(), SYNC_INTERVAL_MS)
    }

    /**
     * Stop the periodic sync interval
     */
    stopSyncInterval() {
        if (this.syncInterval) {
            clearInterval(this.syncInterval)
            this.syncInterval = null
        }
    }

    /**
     * Check if buffer has pending events
     * @returns {boolean}
     */
    hasPendingEvents() {
        return this.events.length > 0
    }

    /**
     * Get count of pending events
     * @returns {number}
     */
    getPendingCount() {
        return this.events.length
    }

    /**
     * Cleanup when leaving session - flushes remaining events
     * @returns {Promise<void>}
     */
    async destroy() {
        this.stopSyncInterval()
        this.clearOutOfAppDebounce()
        this.cleanupPlatformListeners()
        // Avant le dernier envoi, sinon l'épisode part avec le buffer.
        this.closeQualityEpisode("left_live")
        const finalFlush = this.flush()
        // Détaché tout de suite, pas après l'envoi : un écran live remonté dans
        // le même rendu appelle `init()` avant que l'envoi ne revienne, et la
        // remise à zéro tardive tuait le buffer qu'il venait d'ouvrir.
        this.events = []
        this.liveSessionId = null
        this.userId = null
        this.openQualityEpisode = null
        await finalFlush
    }

    /**
     * Force reset without flushing (for error recovery)
     */
    reset() {
        this.stopSyncInterval()
        this.clearOutOfAppDebounce()
        this.cleanupPlatformListeners()
        this.events = []
        this.liveSessionId = null
        this.userId = null
        this.openQualityEpisode = null
    }
}
